import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "@api/useApi";
import { boardKeys, type BoardTemplate } from "@onetasklist/shared";
import { useResolvedBoardTemplates } from "../../board/connections/boardTemplateDefinitions.i18n";
import { useWorkflowSetupStepText } from "./WorkflowSetupStep.i18n";
import type { WizardColumn, WorkflowSetupStepViewModel } from "./WorkflowSetupStep.ViewModel.types";

const columnPalette = [
  "#eef1f5",
  "#dbe7fb",
  "#efe6fb",
  "#fde9d6",
  "#fde4e4",
  "#dbf3e3",
  "#e3f0fb",
];


let columnCounter = 0;

const columnsFromTemplate = (template: BoardTemplate): WizardColumn[] =>
  template.statuses.map((status, i) => ({
    id: `${template.id}-${i}`,
    name: status.name,
    color: status.colour ?? columnPalette[i % columnPalette.length],
    isDone: status.isDone,
    scheduleDateDays: status.scheduleDateDays,
  }));

interface WorkflowSetupStepViewModelProps {
  wizardBoardId: string;
  onFinish: () => Promise<void>;
  onIsSavingChange?: (v: boolean) => void;
}

export const useWorkflowSetupStepViewModel = ({
  wizardBoardId,
  onFinish,
  onIsSavingChange,
}: WorkflowSetupStepViewModelProps): WorkflowSetupStepViewModel => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const text = useWorkflowSetupStepText(t);

  const initialColumns = useMemo<WizardColumn[]>(
    () => [
      { id: "todo", name: t("wizard.step2.defaultColumn.todo"), color: "#eef1f5", isDone: false },
      { id: "inprogress", name: t("wizard.step2.defaultColumn.inProgress"), color: "#dbe7fb", isDone: false },
      { id: "review", name: t("wizard.step2.defaultColumn.review"), color: "#efe6fb", isDone: false },
      { id: "done", name: t("wizard.step2.defaultColumn.done"), color: "#dbf3e3", isDone: true },
    ],
    // Intentionally only runs once on mount — column names are editable user defaults
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const templates = useResolvedBoardTemplates();

  const [boardName, setBoardName] = useState("My Board");
  const [columns, setColumns] = useState<WizardColumn[]>(() => initialColumns);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const selectTemplate = useCallback((template: BoardTemplate): void => {
    setSelectedTemplateId(template.id);
    setColumns(columnsFromTemplate(template));
  }, []);

  const renameColumn = useCallback((id: string, name: string): void => {
    setColumns((cols) => cols.map((c) => (c.id === id ? { ...c, name } : c)));
  }, []);

  const removeColumn = useCallback((id: string): void => {
    setColumns((cols) => {
      const next = cols.filter((c) => c.id !== id);
      const hasDone = next.some((c) => c.isDone);
      if (!hasDone && next.length > 0) {
        return next.map((c, i) => (i === next.length - 1 ? { ...c, isDone: true } : c));
      }
      return next;
    });
  }, []);

  const addColumn = useCallback((): void => {
    columnCounter += 1;
    setColumns((cols) => [
      ...cols,
      {
        id: `new-col-${columnCounter}`,
        name: text.newColumnText,
        color: columnPalette[cols.length % columnPalette.length],
        isDone: false,
      },
    ]);
  }, [text.newColumnText]);

  const moveColumn = useCallback((fromIndex: number, toIndex: number): void => {
    if (fromIndex === toIndex) return;
    setColumns((cols) => {
      const next = [...cols];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const setDoneColumn = useCallback((id: string): void => {
    setColumns((cols) =>
      cols.map((c) => ({ ...c, isDone: c.id === id })),
    );
  }, []);

  const hasDoneColumn = useMemo(
    () => columns.some((c) => c.isDone),
    [columns],
  );

  const finish = useCallback(async (): Promise<void> => {
    setIsSaving(true);
    onIsSavingChange?.(true);
    try {
      const statuses = columns.map((c) => ({
        name: c.name,
        isDone: c.isDone,
        scheduleDateDays: c.scheduleDateDays,
      }));

      // Ensure fallback: last column is Done if none designated
      const hasDone = statuses.some((s) => s.isDone);
      if (!hasDone && statuses.length > 0) {
        statuses[statuses.length - 1].isDone = true;
      }

      await api.boardTemplates.applyToBoard(wizardBoardId, {
        name: boardName,
        statuses,
      });
      // The board and board list were cached under the name the wizard created it with, so the
      // header and tab title kept showing that until a reload.
      await queryClient.invalidateQueries({ queryKey: boardKeys.all });
    } catch {
      // Continue to navigate even if apply fails
    } finally {
      setIsSaving(false);
      onIsSavingChange?.(false);
    }

    await onFinish();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onIsSavingChange intentionally omitted; no tests cover its identity stability
  }, [api.boardTemplates, boardName, columns, onFinish, queryClient, wizardBoardId]);

  return {
    boardName,
    columns,
    templates,
    selectedTemplateId,
    hasDoneColumn,
    isSaving,
    setBoardName,
    selectTemplate,
    renameColumn,
    removeColumn,
    addColumn,
    moveColumn,
    setDoneColumn,
    finish,
    boardNameLabelText: text.boardNameLabelText,
    workflowSectionText: text.workflowSectionText,
    workflowHintText: text.workflowHintText,
    templatesSectionText: text.templatesSectionText,
    templatesHintText: text.templatesHintText,
    addColumnText: text.addColumnText,
    doneColumnLabelText: text.doneColumnLabelText,
    newColumnText: text.newColumnText,
  };
};
